// JOBE-0167.5: ForgetLogTabBody — chronological forget-event feed.
//
// Data source: GET /api/memory/forget-log with cursor pagination.
// Fixture: memory.mock.forgetting (registered in mock-wcc.js per JOBE-0157).

import { useEffect, useState, useCallback } from 'react';
import { useFlagStore } from '../../lib/useFlagStore.js';
import { flaggedFetch } from '../../lib/flags.js';
import { ForgetLogRow } from './ForgetLogRow.jsx';

function summarize(events) {
  let tomb = 0;
  let hard = 0;
  for (const e of events) {
    if (e.lifecycle === 'hard_deleted') hard += 1;
    else tomb += 1;
  }
  return { n: events.length, tomb, hard };
}

export function ForgetLogTabBody() {
  const overrides = useFlagStore((s) => s.overrides);
  const [state, setState] = useState('loading');
  const [events, setEvents] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);

  const load = useCallback(async (append = false, cursor = null) => {
    setState(append ? 'loaded' : 'loading');
    // Fixture path.
    const fx = flaggedFetch('memory.mock.forgetting', overrides);
    if (fx !== undefined) {
      const list = Array.isArray(fx) ? fx : (fx.events || []);
      setEvents(list);
      setNextCursor(Array.isArray(fx) ? null : (fx.nextCursor || null));
      setState('loaded');
      return;
    }
    // Real path.
    const url = '/api/memory/forget-log' + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : '');
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`forget-log ${resp.status}`);
      const json = await resp.json();
      const rows = json.events || [];
      setEvents((prev) => append ? [...prev, ...rows] : rows);
      setNextCursor(json.nextCursor || null);
      setState('loaded');
    } catch {
      setState('error');
    }
  }, [overrides]);

  useEffect(() => { load(false, null); }, [load]);

  if (state === 'loading') {
    return <div className="forget-log-body" data-testid="forget-log-body"><div data-testid="forget-log-loading">Loading…</div></div>;
  }
  if (state === 'error') {
    return <div className="forget-log-body" data-testid="forget-log-body"><div data-testid="forget-log-error">Could not load forget log.</div></div>;
  }
  if (events.length === 0) {
    return (
      <div className="forget-log-body" data-testid="forget-log-body">
        <div data-testid="forget-log-empty">No forget events in the selected window.</div>
      </div>
    );
  }
  const { n, tomb, hard } = summarize(events);
  return (
    <div className="forget-log-body" data-testid="forget-log-body">
      <div className="forget-log-body__summary" data-testid="forget-log-summary">
        {n} forget events in the last 30 days · {tomb} tombstoned · {hard} hard-deleted
      </div>
      <ul className="forget-log-body__list" data-testid="forget-log-list">
        {events.map((e, i) => (
          <ForgetLogRow key={e.id || `${e.urn}-${e.ts}-${i}`} event={e} />
        ))}
      </ul>
      {nextCursor && (
        <div className="forget-log-body__more">
          <button
            type="button"
            className="forget-log-body__load-more"
            data-testid="forget-log-load-more"
            onClick={() => load(true, nextCursor)}
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

export default ForgetLogTabBody;
